import React from 'react';
import { Pagination, Table } from '@brasshq/ui-kit';
import cx from 'classnames';
import { EmptyTableStateIllustration } from 'src/assets/svgs';
import { DIALOG_DATA_DEFAULTS } from 'src/data/constants';
import RestrictionGuard from 'src/modules/restriction-guard';
import ActionButton from 'src/sharedComponents/ActionButton';
import Dialog from 'src/sharedComponents/Dialog';
import EmptyScreen from 'src/sharedComponents/EmptyScreen';
import {
  HandlePermission,
  Pagination as PaginationType,
  Role,
} from 'src/types';
import { TeamMember } from 'src/types/entities/administration';

import { getTeamMatesDialogData, getTeamMatesTableConfig } from '../../config';
import RolePrivilegesFlyout from '../../flyouts/RolePrivilegesFlyout';
import TeamMatesDetailsFlyout from '../../flyouts/TeamMatesDetailsFlyout';
import {
  deleteTeamInvite,
  removeTeamMember,
  resendTeamInvite,
} from '../../functions';
import { RDTTeammatesTabFetchParams, TeamMatesTableState } from '../../types';

import styles from './TeamMatesTable.module.scss';

interface TeamMatesTableProps {
  activeTabIndex: number;
  data: TeamMember[];
  loading: boolean;
  roles?: Role[];
  refresh: () => void;
  paginate: (filtersUpdate: Partial<RDTTeammatesTabFetchParams>) => void;
  pagination: PaginationType;
  handlePermissions: HandlePermission;
}

const TeamMatesTable: React.FC<TeamMatesTableProps> = ({
  activeTabIndex,
  data,
  loading,
  roles,
  refresh,
  paginate,
  pagination,
  handlePermissions,
}) => {
  const [state, setState] = React.useState<TeamMatesTableState>({
    activeRowData: null,
    showDetailsFlyout: false,
    showPrivilegeFlyout: false,
    showDialog: false,
    dialogType: null,
  });

  const activeRole = roles?.find(
    ({ id }) => id === state.activeRowData?.role?.data.id,
  );

  const handleStateUpdate = (newState: Partial<TeamMatesTableState>) =>
    setState(state => ({ ...state, ...newState }));

  const { actions, dataSchema, emptyStateProps } = getTeamMatesTableConfig(
    activeTabIndex,
    {
      handleStateUpdate,
      handlePermissions,
    },
  );

  const dialogData = state.dialogType
    ? getTeamMatesDialogData({
        removeMember: removeTeamMember,
        resendInvite: resendTeamInvite,
        deleteInvite: deleteTeamInvite,
      })[state.dialogType]
    : DIALOG_DATA_DEFAULTS;

  const renderEmptyState = () =>
    emptyStateProps ? (
      <EmptyScreen
        className={styles.TeamMatesTable_emptyState}
        icon={<EmptyTableStateIllustration />}
        {...emptyStateProps}
      />
    ) : undefined;

  return (
    <div className={styles.TeamMatesTable_wrapper}>
      <Table
        className={cx(styles.TeamMatesTable, {
          [styles.TeamMatesTable_no_border_top]:
            activeTabIndex < 2 || (activeTabIndex === 2 && !data?.length),
          [styles.TeamMatesTable_emptyTable]: !data?.length,
        })}
        config={{
          dataSchema: dataSchema,
          actionsSchema: actions,
        }}
        data={data}
        isLoading={loading}
        onRowClick={(activeRowData: TeamMember) => {
          activeTabIndex === 0 &&
            handleStateUpdate({
              showPrivilegeFlyout: true,
              activeRowData,
            });
        }}
        renderEmptyState={renderEmptyState}
      />
      {!!data?.length && (
        <Pagination
          className={styles.PaymentsTable_pagination}
          limit={pagination?.per_page}
          paginate={(page: number, limit: number) => {
            paginate({ page, limit });
          }}
          totalPage={pagination?.total_pages}
          currentPage={pagination?.current_page}
        />
      )}
      <RestrictionGuard
        requiredPermissions={['team.manage', 'team.invite']}
        strict
      >
        <ActionButton
          onClick={() => {
            handleStateUpdate({
              showDetailsFlyout: true,
              activeRowData: null,
            });
          }}
        />
      </RestrictionGuard>
      <TeamMatesDetailsFlyout
        isOpen={state.showDetailsFlyout}
        data={state.activeRowData}
        handleClose={() =>
          handleStateUpdate({
            showDetailsFlyout: false,
            activeRowData: null,
          })
        }
        refresh={refresh}
        roles={roles}
      />
      <RolePrivilegesFlyout
        role={activeRole}
        isOpen={state.showPrivilegeFlyout}
        handleClose={() => {
          handleStateUpdate({
            showPrivilegeFlyout: false,
            activeRowData: null,
          });
        }}
      />
      <Dialog
        show={state.showDialog && !!state.dialogType}
        handleClose={() => {
          handleStateUpdate({
            showDialog: false,
            dialogType: null,
          });
        }}
        data={state.activeRowData!}
        refresh={refresh}
        {...dialogData}
      />
    </div>
  );
};

export default TeamMatesTable;
